import type { components } from "@majitap/sdk";
import type { MapTank } from "@majitap/ui";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";
import type { Tone } from "./labels";

type Schemas = components["schemas"];
export type Sensor = Schemas["Sensor"];
export type SensorStatus = Sensor["status"];

export const SENSOR_STATUS: Record<SensorStatus, [label: string, tone: Tone]> = {
  active: ["Reading", "success"],
  inactive: ["Off", "neutral"],
  faulty: ["Faulty", "danger"],
};

/** Low while a low-level episode is open: from the first reading under the tank's threshold until it's 5 points above. */
export const isLow = (s: Pick<Sensor, "low_alert_at">) => s.low_alert_at !== null;

/** "18.00% · 1,800 L", or why there's no level. */
export function levelText(s: Pick<Sensor, "latest" | "status">): string {
  if (!s.latest) return s.status === "active" ? "No reading yet" : "No reading";
  return [
    `${s.latest.level_percent}%`,
    s.latest.litres !== null ? `${s.latest.litres.toLocaleString("en-KE")} L` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** The customer's or business's page, on its Places and tanks tab. */
export function ownerHref(owner: { type: "customer" | "business"; id: string } | null | undefined): string | null {
  if (!owner) return null;
  return owner.type === "customer" ? `/customers/${owner.id}?tab=places` : `/businesses/${owner.id}?tab=places`;
}

export const sensorActions = {
  register: (body: Schemas["CreateSensorBody"]) => ok(api.POST("/v1/admin/sensors", { body })),
  update: (id: string, body: Schemas["UpdateSensorBody"]) =>
    ok(api.PATCH("/v1/admin/sensors/{id}", { params: { path: { id } }, body })),
  poll: () => ok(api.POST("/v1/admin/sensors/poll")),
};

/** Every sensor, for the live map's tanks (P8-51): page after page of the API's largest page, read again each minute. */
export const mapSensorsQuery = queryOptions({
  queryKey: ["sensors", "map"],
  refetchInterval: 60_000,
  queryFn: async () => {
    const all: Sensor[] = [];
    let cursor: string | undefined;
    do {
      const page = await ok(api.GET("/v1/admin/sensors", { params: { query: { limit: 100, cursor } } }));
      all.push(...page.data);
      cursor = page.next_cursor ?? undefined;
    } while (cursor);
    return all;
  },
});

/** "14.00" → "14", "14.50" → "14.5": a percentage as people write it. */
export const percentText = (v: string) => (v.includes(".") ? v.replace(/0+$/, "").replace(/\.$/, "") : v);

/** Where the tank is said to be: its owner, else its place. */
export const tankName = (s: Sensor) => s.tank?.owner?.name ?? s.tank?.location?.label ?? s.label ?? s.external_id;

/** A sensor's tank on the map, or null when it isn't on a tank with a point. */
export function tankOnMap(s: Sensor): MapTank | null {
  const at = s.tank?.location;
  if (at?.latitude == null || at.longitude == null) return null;
  const reading = s.status === "active" ? s.latest : null;
  // number-ok: a gauge's height and a rounded percentage on the map, not money
  const level = reading ? Math.round(Number(reading.level_percent)) : null;
  const state = level === null ? "none" : isLow(s) ? "low" : "ok";
  const name = tankName(s);
  return {
    id: s.id,
    lng: at.longitude,
    lat: at.latitude,
    state,
    gauge: level,
    text: level === null ? "—" : state === "low" ? `${level}% · ${name}` : `${level}%`,
    title: `${name}, ${levelText(s)}${state === "low" ? ", low" : ""}`,
  };
}
